import React, { useState, useEffect, useMemo } from "react";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import handleService from "../../services/apiService.ts";
import { formatarCNPJ } from "../../utils/cnpj.util.ts";
import { MethodsEnum } from "../../enums/Methods.ts";
import UpdateHospitalDrawer from "../../components/Modals/UpdateHospitalDrawer/index.tsx";
import CreateHospitalDrawer from "../../components/Modals/CreateHospitalDrawer/index.tsx";
import ConfirmDeleteDialog from "../../components/Modals/ConfirmDeleteDialog/index.tsx";
import { Response } from "../../types/Api.ts";
import Button from "../../ui/Button.tsx";
import SearchField from "../../ui/SearchField.tsx";
import EmptyState from "../../ui/EmptyState.tsx";
import SkeletonRows from "../../ui/SkeletonRows.tsx";
import "../../styles/design-system.css";

interface Hospital {
  id: string;
  nome: string;
  cnpj: string;
}

const ListagemHospital = () => {
  const [hospitais, setHospitais] = useState<Hospital[]>([]);
  const [busca, setBusca] = useState("");
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isViewOpen, setIsViewOpen] = useState(false);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [currentHospital, setCurrentHospital] = useState<Hospital | null>(null);
  const [hospitalParaExcluir, setHospitalParaExcluir] = useState<Hospital | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isExcluindo, setIsExcluindo] = useState(false);

  useEffect(() => {
    fetchHospitais();
  }, []);

  const fetchHospitais = async () => {
    try {
      setIsLoading(true);
      const response = await handleService({ endpoint: "/hospital" });
      const lista: Hospital[] = Array.isArray(response) ? response : response.data || [];
      setHospitais(lista);
    } catch (error) {
    } finally {
      setIsLoading(false);
    }
  };

  const handleVisualizar = (hospital: Hospital) => {
    setCurrentHospital(hospital);
    setIsViewOpen(true);
  };

  const handleEditar = (hospital: Hospital) => {
    setCurrentHospital(hospital);
    setIsEditOpen(true);
  };

  const handleCloseEdit = () => {
    setIsEditOpen(false);
    setCurrentHospital(null);
  };

  const handleCloseView = () => {
    setIsViewOpen(false);
    setCurrentHospital(null);
  };

  const onUpdate = (response: Response<Hospital>) => {
    const hospital = response.data;
    if (hospital) {
      setHospitais((prev) => prev.map((h) => (h.id === hospital.id ? hospital : h)));
      setIsEditOpen(false);
    }
  };

  const onCreated = (response: Response<Hospital>) => {
    const hospital = response.data;
    if (hospital) {
      setHospitais((prev) => [...prev, hospital]);
      setIsCreateOpen(false);
    }
  };

  const handleConfirmarExclusao = async () => {
    if (!hospitalParaExcluir) return;
    try {
      setIsExcluindo(true);
      await handleService({ endpoint: `/hospital/${hospitalParaExcluir.id}`, method: MethodsEnum.DELETE });
      setHospitais((prev) => prev.filter((h) => h.id !== hospitalParaExcluir.id));
      setHospitalParaExcluir(null);
    } catch (error) {
    } finally {
      setIsExcluindo(false);
    }
  };

  const filtrados = useMemo(
    () => hospitais.filter((h) => h.nome?.toLowerCase().includes(busca.toLowerCase())),
    [hospitais, busca]
  );

  return (
    <div className="ds">
      <div className="ds-page-head">
        <div>
          <h1>Hospitais</h1>
          <p>Visualize e gerencie os hospitais da sua clínica.</p>
        </div>
        <div className="ds-page-head-actions">
          <Button variant="primary" icon={<AddCircleOutlineIcon fontSize="small" />} onClick={() => setIsCreateOpen(true)}>
            Novo hospital
          </Button>
        </div>
      </div>

      <div className="ds-toolbar">
        <SearchField value={busca} onChange={setBusca} placeholder="Buscar por nome..." />
      </div>

      <div className="ds-table-wrap">
        <table className="ds-table">
          <thead>
            <tr>
              <th>Nome do hospital</th>
              <th>CNPJ</th>
              <th>Ações</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <SkeletonRows columns={3} />
            ) : filtrados.length === 0 ? (
              <tr>
                <td colSpan={3}>
                  <EmptyState title="Nenhum hospital encontrado" description="Cadastre o primeiro hospital para começar." actionLabel="Novo hospital" onAction={() => setIsCreateOpen(true)} />
                </td>
              </tr>
            ) : (
              filtrados.map((hospital) => {
                return (
                  <tr key={hospital.id}>
                    <td>
                      <div className="ds-cell-title">{hospital.nome}</div>
                    </td>
                    <td>{formatarCNPJ(hospital?.cnpj)}</td>
                    <td>
                      <div className="ds-row-actions">
                        <button className="ds-icon-btn" onClick={() => handleVisualizar(hospital)} title="Visualizar">
                          <VisibilityOutlinedIcon fontSize="small" />
                        </button>
                        <button className="ds-icon-btn" onClick={() => handleEditar(hospital)} title="Editar">
                          <EditOutlinedIcon fontSize="small" />
                        </button>
                        <button className="ds-icon-btn" onClick={() => setHospitalParaExcluir(hospital)} title="Excluir">
                          <DeleteOutlineIcon fontSize="small" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <UpdateHospitalDrawer isOpen={isEditOpen} onClose={handleCloseEdit} hospital={currentHospital} onUpdate={onUpdate} />
      <UpdateHospitalDrawer isOpen={isViewOpen} onClose={handleCloseView} hospital={currentHospital} readOnly />
      <CreateHospitalDrawer open={isCreateOpen} onClose={() => setIsCreateOpen(false)} onCreated={onCreated} />

      <ConfirmDeleteDialog
        open={!!hospitalParaExcluir}
        title="Excluir Hospital"
        message="Tem certeza que deseja excluir este hospital cadastrado? Esta ação não poderá ser desfeita."
        loading={isExcluindo}
        onCancel={() => setHospitalParaExcluir(null)}
        onConfirm={handleConfirmarExclusao}
      />
    </div>
  );
};

export default ListagemHospital;
